import { useMemo } from 'react';
import type { AntiDetectionSource } from './catalog';

/**
 * Dependency-free stacked-area SVG used by the drill-down route.
 * Renders one chart per outcome (fire / refuse / degrade) with up
 * to four stacked series — one per source-table (audit_log /
 * connectivity_event / worker_log / fire_marker_event) — per spec
 * §3.3.
 *
 * Like Sparkline.tsx this avoids pulling in a chart library so the
 * BO security review surface stays minimal. The path-builder is a
 * pure function exported separately for unit-testability.
 */

const SOURCE_COLORS: Record<AntiDetectionSource, string> = {
  audit_log: '#1677ff',
  connectivity_event: '#52c41a',
  worker_log: '#faad14',
  fire_marker_event: '#722ed1',
};

const SOURCE_LABELS: Record<AntiDetectionSource, string> = {
  audit_log: '審計日誌',
  connectivity_event: '連線事件',
  worker_log: 'Worker 日誌',
  fire_marker_event: 'Fire marker',
};

export interface StackedSeries {
  source: AntiDetectionSource;
  values: number[];
}

export interface StackedAreaChartProps {
  /** Series, each with one value per hour bucket. */
  series: StackedSeries[];
  /** ISO labels for hour buckets — used in x-axis tick rendering. */
  bucketLabels: string[];
  width?: number;
  height?: number;
  ariaLabel: string;
}

export function StackedAreaChart({
  series,
  bucketLabels,
  width = 720,
  height = 200,
  ariaLabel,
}: StackedAreaChartProps) {
  const PADDING = { top: 16, right: 12, bottom: 28, left: 36 };
  const innerWidth = width - PADDING.left - PADDING.right;
  const innerHeight = height - PADDING.top - PADDING.bottom;

  const { layers, max, bucketCount } = useMemo(
    () => stackSeries(series),
    [series]
  );

  if (bucketCount === 0) {
    return (
      <svg
        role="img"
        aria-label={ariaLabel}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ display: 'block' }}
      >
        <text
          x={width / 2}
          y={height / 2}
          textAnchor="middle"
          fontSize={12}
          fill="#999"
        >
          無資料
        </text>
      </svg>
    );
  }

  const stepX = bucketCount > 1 ? innerWidth / (bucketCount - 1) : innerWidth;
  const safeMax = max > 0 ? max : 1;

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block' }}
    >
      {/* Y-axis baseline + grid */}
      <line
        x1={PADDING.left}
        y1={PADDING.top + innerHeight}
        x2={PADDING.left + innerWidth}
        y2={PADDING.top + innerHeight}
        stroke="#d9d9d9"
        strokeWidth={1}
      />
      <line
        x1={PADDING.left}
        y1={PADDING.top}
        x2={PADDING.left}
        y2={PADDING.top + innerHeight}
        stroke="#d9d9d9"
        strokeWidth={1}
      />

      {/* Y-axis max tick */}
      <text
        x={PADDING.left - 6}
        y={PADDING.top + 4}
        textAnchor="end"
        fontSize={10}
        fill="#666"
      >
        {safeMax}
      </text>
      <text
        x={PADDING.left - 6}
        y={PADDING.top + innerHeight}
        textAnchor="end"
        fontSize={10}
        fill="#666"
      >
        0
      </text>

      {/* X-axis edge ticks */}
      {bucketLabels.length > 0 && (
        <>
          <text
            x={PADDING.left}
            y={PADDING.top + innerHeight + 14}
            textAnchor="start"
            fontSize={10}
            fill="#666"
          >
            {formatBucketLabel(bucketLabels[0])}
          </text>
          <text
            x={PADDING.left + innerWidth}
            y={PADDING.top + innerHeight + 14}
            textAnchor="end"
            fontSize={10}
            fill="#666"
          >
            {formatBucketLabel(bucketLabels[bucketLabels.length - 1])}
          </text>
        </>
      )}

      {/* Stacked areas (paint topmost layer first so lower layers sit beneath) */}
      {layers.map((layer) => {
        const top = layer.cumulativeAfter;
        const bottom = layer.cumulativeBefore;
        const areaPath = buildAreaPath(
          top,
          bottom,
          stepX,
          safeMax,
          innerHeight,
          PADDING.left,
          PADDING.top
        );
        return (
          <path
            key={layer.source}
            d={areaPath}
            fill={SOURCE_COLORS[layer.source]}
            fillOpacity={0.7}
            stroke={SOURCE_COLORS[layer.source]}
            strokeWidth={1}
          />
        );
      })}

      {/* Legend */}
      {layers.map((layer, idx) => (
        <g
          key={`legend-${layer.source}`}
          transform={`translate(${PADDING.left + idx * 110}, ${
            PADDING.top - 8
          })`}
        >
          <rect width={8} height={8} fill={SOURCE_COLORS[layer.source]} />
          <text x={12} y={8} fontSize={10} fill="#333">
            {SOURCE_LABELS[layer.source]}
          </text>
        </g>
      ))}
    </svg>
  );
}

interface StackLayer {
  source: AntiDetectionSource;
  cumulativeBefore: number[];
  cumulativeAfter: number[];
}

/**
 * Pure path stacking helper. Returns layers ordered from bottom
 * (drawn first) to top (drawn last) plus the overall max so the
 * caller can normalise the Y-axis. Exported for unit testing.
 */
export function stackSeries(series: StackedSeries[]): {
  layers: StackLayer[];
  max: number;
  bucketCount: number;
} {
  const bucketCount = series.reduce(
    (m, s) => Math.max(m, s.values.length),
    0
  );
  if (bucketCount === 0) {
    return { layers: [], max: 0, bucketCount: 0 };
  }
  const cumulative = new Array<number>(bucketCount).fill(0);
  const layers: StackLayer[] = [];
  for (const s of series) {
    const before = cumulative.slice();
    const after = cumulative.slice();
    for (let i = 0; i < bucketCount; i++) {
      const v = s.values[i] ?? 0;
      after[i] = before[i] + Math.max(0, v);
    }
    layers.push({
      source: s.source,
      cumulativeBefore: before,
      cumulativeAfter: after,
    });
    for (let i = 0; i < bucketCount; i++) {
      cumulative[i] = after[i];
    }
  }
  const max = cumulative.reduce((m, v) => Math.max(m, v), 0);
  return { layers, max, bucketCount };
}

function buildAreaPath(
  top: number[],
  bottom: number[],
  stepX: number,
  max: number,
  innerHeight: number,
  offsetX: number,
  offsetY: number
): string {
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i < top.length; i++) {
    const x = offsetX + i * stepX;
    const y = offsetY + innerHeight - (top[i] / max) * innerHeight;
    pts.push({ x, y });
  }
  for (let i = bottom.length - 1; i >= 0; i--) {
    const x = offsetX + i * stepX;
    const y = offsetY + innerHeight - (bottom[i] / max) * innerHeight;
    pts.push({ x, y });
  }
  if (!pts.length) return '';
  return (
    'M ' +
    pts
      .map((p, idx) => (idx === 0 ? '' : 'L ') + `${r2(p.x)} ${r2(p.y)}`)
      .join(' ') +
    ' Z'
  );
}

function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

function formatBucketLabel(iso: string): string {
  const m = /T(\d{2}):/.exec(iso);
  if (!m) return iso.slice(0, 16);
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!dateMatch) return `${m[1]}:00`;
  return `${dateMatch[2]}/${dateMatch[3]} ${m[1]}:00`;
}
